import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { jwtDecode } from "jwt-decode";
import { Claims } from "../../types/Claims";
import { ScreeningItem } from "../../types/Screening";
import { successToast, errorToast } from "@idwal/idwal-react-components";
import { postReportRequest } from "../../services/ScreeningService";
import { convertDateStringToUkLocale } from "../../utils/dates";
import "./RequestReportModal.css";

type Props = {
    data: ScreeningItem;
    onClose: () => void;
};

export const RequestReportModal = ({ data, onClose }: Props) => {
    const { t } = useTranslation("locale");
    const { user } = useAuthenticator((context) => [context.user]);

    let name = "";
    let organisation = "";
    let email = "";

    if (user) {
        // @ts-ignore signInUserSession is not on Amplify's CognitoUser type
        const token = user.signInUserSession.idToken.jwtToken;
        const claims = jwtDecode<Claims>(token);
        name = decodeURIComponent(claims["custom:full_name"] ?? "");
        organisation = decodeURIComponent(claims["custom:organisation"] ?? "");
        email = user.attributes?.email ?? "";
    }

    const [nameValue, setNameValue] = useState(name);
    const [organisationValue, setOrganisationValue] = useState(organisation);
    const [emailValue, setEmailValue] = useState(email);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<{
        name?: string;
        organisation?: string;
        email?: string;
    }>({});

    const isValidEmail = (v: string) => {
        const i = v.indexOf("@");
        return i > 0 && v.indexOf(".", i) > i + 1;
    };

    const forSale = !!data.for_sale;

    const onSubmit = async () => {
        const newErrors = {
            name: !nameValue ? t("validation.required.name") : undefined,
            organisation: !organisationValue
                ? t("validation.required.organisation")
                : undefined,
            email: !isValidEmail(emailValue)
                ? t("validation.required.email")
                : undefined
        };
        setErrors(newErrors);
        if (newErrors.name || newErrors.organisation || newErrors.email) return;
        setSubmitting(true);
        try {
            await postReportRequest({
                imo: data.vessel_imo,
                vesselName: data.vessel_name,
                gradeDate: data.grade_date,
                name: nameValue,
                email: emailValue,
                organisation: organisationValue,
                reason,
                forSale,
                submittedByName: name,
                submittedByEmail: email,
                submittedByOrganisation: organisation
            });
            successToast(t("screening.requestReport.successToast"));
            onClose();
        } catch {
            errorToast(t("screening.requestReport.errorToast"));
        } finally {
            setSubmitting(false);
        }
    };

    const footer = (
        <div>
            <Button
                label={t("screening.requestReport.cancel")}
                link
                onClick={onClose}
            />
            <Button
                label={t("screening.requestReport.submit")}
                onClick={onSubmit}
                loading={submitting}
            />
        </div>
    );

    return (
        <Dialog
            header={t("screening.requestReport.title")}
            visible
            onHide={onClose}
            footer={footer}
            draggable={false}
            style={{ width: "min(680px, 95vw)" }}
            className="request-report-dialog"
        >
            {forSale && (
                <div className="for-sale-banner">
                    <span className="for-sale-badge">
                        {t("screening.requestReport.forSaleTitle")}
                    </span>
                    <span className="for-sale-banner-text">
                        {data.sales_start_date
                            ? t("screening.requestReport.forSaleBody", {
                                  date: convertDateStringToUkLocale(
                                      data.sales_start_date
                                  )
                              })
                            : t("screening.requestReport.forSaleBodyNoDate")}
                    </span>
                </div>
            )}
            <p className="request-report-body">
                <strong>{t("screening.requestReport.body_1")}</strong>{" "}
                {t("screening.requestReport.body_2")}
            </p>
            <div className="flex flex-column gap-3 mt-3">
                <div className="flex align-items-center gap-3">
                    <label className="w-8rem request-report-label">
                        {t("screening.requestReport.name")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            className={`w-full${
                                errors.name ? " p-invalid" : ""
                            }`}
                        />
                        {errors.name && (
                            <small className="p-error">{errors.name}</small>
                        )}
                    </div>
                </div>
                <div className="flex align-items-center gap-3">
                    <label className="w-8rem request-report-label">
                        {t("screening.requestReport.organisation")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={organisationValue}
                            onChange={(e) =>
                                setOrganisationValue(e.target.value)
                            }
                            className={`w-full${
                                errors.organisation ? " p-invalid" : ""
                            }`}
                        />
                        {errors.organisation && (
                            <small className="p-error">
                                {errors.organisation}
                            </small>
                        )}
                    </div>
                </div>
                <div className="flex align-items-center gap-3">
                    <label className="w-8rem request-report-label">
                        {t("screening.requestReport.email")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                            className={`w-full${
                                errors.email ? " p-invalid" : ""
                            }`}
                        />
                        {errors.email && (
                            <small className="p-error">{errors.email}</small>
                        )}
                    </div>
                </div>
                <div className="flex align-items-start gap-3">
                    <label className="w-8rem request-report-label">
                        {t("screening.requestReport.reason")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputTextarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            autoFocus
                            rows={4}
                            className="w-full"
                            placeholder="e.g. Reason for requesting access, intended use, etc."
                        />
                        <small className="request-report-reason-hint">
                            {t("screening.requestReport.reasonHint")}
                        </small>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
